import React, { useCallback, useMemo, useState } from 'react';
import {
    Button,
    Checkbox,
    CircularProgress,
    Collapse,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
} from '@material-ui/core';
import i18n from 'i18n-js';
import DateFilters from 'components/restaurantInformation/finance/components/DateFIlters';
import { useStyles } from '../styles';
import agent from 'api/agent';
import { formatDateForServer, parseFileNameFromContentDisposition } from 'helpers/helperFunctions';
import { SortOrder } from 'constants/types';
import { DishStock, ExportOwnerDishModel, IdName, RestaurantGroup } from 'api/models';
import { useDispatch } from 'react-redux';
import globalStore from 'store/globalStore';
import { NothingFoundMessage, SearchInput, Text } from 'components/shared';
import clsx from 'clsx';
import { SelectionMap, StockTableSearchFilter } from '../interfaces';
import StockRestaurantSelect from './StockRestaurantSelect';
import {
    AssessmentOutlined as AssessmentOutlinedIcon,
    Close as CloseIcon,
    GetApp as GetAppIcon,
} from '@material-ui/icons';
import { useScreenSize } from 'hooks/useScreenSize';
import { useAppSelector } from 'index';
import { DateFilterType } from 'store/uiStore/interfaces';

const UNGROUPED_KEY = '__ungrouped__';

export default function RestaurantStockTab({ onLoaded }: { onLoaded: () => void }) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const [dateFilter, setDateFilter] = useState(DateFilterType.DAY);
    const [dishes, setDishes] = useState<DishStock[]>([]);
    const [order, setOrder] = useState<SortOrder>('asc');
    const [orderBy, setOrderBy] = useState<keyof DishStock | ''>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [groups, setGroups] = useState<RestaurantGroup[]>([]);
    const [allRestaurants, setAllRestaurants] = useState<IdName[]>([]);
    const [selectionMap, setSelectionMap] = useState<SelectionMap>(new Map());
    const [filter, setFilter] = useState<StockTableSearchFilter>({
        name: '',
        externalId: '',
        supplierName: '',
        catalogName: '',
    });
    const [dateRange, setDateRange] = useState<{ from: Date; to: Date }>({
        from: new Date(),
        to: new Date(),
    });
    const [isSelectMode, setSelectMode] = useState<boolean>(false);
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const { screenWidth } = useScreenSize();
    const isNavOpen = useAppSelector((state) => state.ui.navBarOpen);
    const isDesktop = screenWidth > 1440;
    const textLimit = {
        L: isDesktop ? 35 : isNavOpen ? 15 : 25,
        M: isDesktop ? 25 : 12,
        S: isDesktop ? 20 : 12,
    };

    const selectedRestaurantIds = useMemo(() => {
        const result = new Set<string>();

        selectionMap.forEach((set) => {
            set.forEach((id) => result.add(id));
        });

        return result;
    }, [selectionMap]);

    const extendedGroups = useMemo(
        () => prepareExtendedGroups(groups, allRestaurants),
        [groups, allRestaurants]
    );

    const filteredDishes = useMemo(() => {
        return dishes.filter(filterData);
    }, [dishes, filterData, selectionMap]);

    function prepareExtendedGroups(
        groups: RestaurantGroup[],
        allRestaurants: IdName[]
    ): RestaurantGroup[] {
        const groupedIds = new Set<string>();

        for (const g of groups) {
            for (const r of g.restaurants) {
                groupedIds.add(r.id);
            }
        }

        const standalone: IdName[] = [];
        for (const r of allRestaurants) {
            if (!groupedIds.has(r.id)) {
                standalone.push(r);
            }
        }

        if (standalone.length === 0) return groups;

        return [
            ...groups,
            {
                id: UNGROUPED_KEY,
                name: i18n.t('common.noGroup'),
                restaurants: standalone,
            },
        ];
    }

    function buildSelectionMap(groups: RestaurantGroup[]): SelectionMap {
        return new Map(groups.map((g) => [g.id, new Set(g.restaurants.map((r) => r.id))]));
    }

    function getData(from: Date, to: Date) {
        setIsLoading(true);
        agent.Restaurants.GetOwnerRestaurantsStock(
            formatDateForServer(from),
            formatDateForServer(to)
        )
            .then((res) => {
                setGroups(res.groups);
                setAllRestaurants(res.restaurants);
                setDishes(res.products);
                setSelectionMap((prev) => {
                    if (prev.size > 0) {
                        return prev;
                    }

                    const ext = prepareExtendedGroups(res.groups, res.restaurants);
                    return buildSelectionMap(ext);
                });
            })
            .catch((err) => {
                dispatch(
                    globalStore.actionCreators.showToaster('error', i18n.t('common.somethingWrong'))
                );
            })
            .finally(() => {
                setIsLoading(false);
                onLoaded();
            });
    }

    const handleRequestSort = (property: keyof DishStock) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    function sortData(a: DishStock, b: DishStock) {
        if (!orderBy) return 0;
        const v1 = a[orderBy];
        const v2 = b[orderBy];
        let result = 0;
        const isEmpty1 = v1 === null || v1 === undefined || v1 === '';
        const isEmpty2 = v2 === null || v2 === undefined || v2 === '';

        if (isEmpty1 && isEmpty2) return 0;
        if (isEmpty1) return 1;
        if (isEmpty2) return -1;

        if (typeof v1 === 'string' && typeof v2 === 'string') {
            result = v1.localeCompare(v2);
        } else if (typeof v1 === 'number' && typeof v2 === 'number') {
            result = v1 - v2;
        }

        return order === 'asc' ? result : -result;
    }

    function normalize(value?: string | null) {
        return value?.trim().toLowerCase() || '';
    }

    function filterData(dish: DishStock) {
        if (dish.groupId !== null) {
            const groupSet = selectionMap.get(dish.groupId);
            if (!groupSet?.has(dish.restaurantId)) {
                return false;
            }
        } else if (!selectedRestaurantIds.has(dish.restaurantId)) {
            return false;
        }

        const name = normalize(filter.name);
        const externalId = normalize(filter.externalId);
        const supplierName = normalize(filter.supplierName);
        const catalogName = normalize(filter.catalogName);

        if (name && !normalize(dish.name).includes(name)) {
            return false;
        }
        if (externalId && !normalize(dish.externalId?.toString()).includes(externalId)) {
            return false;
        }
        if (supplierName && !normalize(dish.supplierName).includes(supplierName)) {
            return false;
        }
        if (catalogName && !normalize(dish.catalogName).includes(catalogName)) {
            return false;
        }

        return true;
    }

    function makeKey(restaurantId: string, id: string): string {
        return `${restaurantId}_${id}`;
    }

    function handleSelectAll(checked: boolean): void {
        if (checked) {
            setSelected(new Set(filteredDishes.map((d) => makeKey(d.restaurantId, d.id))));
        } else {
            setSelected(new Set());
        }
    }

    function handleToggleSelectMode(state: boolean): void {
        setSelectMode(state);
        if (!state) {
            setTimeout(() => {
                handleSelectAll(false);
            }, 200);
        }
    }

    function handleSelectDish(checked: boolean, dish: DishStock): void {
        setSelected((prev) => {
            const newSet = new Set(prev);
            if (checked) {
                newSet.add(makeKey(dish.restaurantId, dish.id));
            } else {
                newSet.delete(makeKey(dish.restaurantId, dish.id));
            }

            return newSet;
        });
    }

    function handleExportReport(): void {
        const from = formatDateForServer(dateRange.from);
        const to = formatDateForServer(dateRange.to);
        const mappedDishes = filteredDishes
            .filter((d) => selected.has(makeKey(d.restaurantId, d.id)))
            .map<ExportOwnerDishModel>((d) => {
                return {
                    id: d.id,
                    restaurantId: d.restaurantId,
                    groupId: d.groupId,
                };
            });
        const selectedGroups = groups.filter((g) => selectionMap.has(g.id)).map((g) => g.name);
        const groupName = selectedGroups.length > 0 ? selectedGroups.join(', ') : '-';

        dispatch(globalStore.actionCreators.showSpiner());
        agent.Restaurants.ExportOwnerStock(from, to, groupName, mappedDishes)
            .then((res) => {
                const blob = res.data;
                const downloadUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                const filename = parseFileNameFromContentDisposition(
                    res.headers['content-disposition'],
                    'Stock_Report',
                    'xlsx'
                );
                link.href = downloadUrl;
                link.setAttribute('download', filename);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(downloadUrl);

                const message = `${filename} ${i18n.t('messages.downloadedSuccessfully')}`;
                dispatch(globalStore.actionCreators.showToaster('success', message));
                handleToggleSelectMode(false);
            })
            .catch((e) => {
                dispatch(globalStore.actionCreators.showToaster('error', e));
            })
            .finally(() => dispatch(globalStore.actionCreators.hideSpiner()));
    }

    const handleDateChange = useCallback(
        (_, __, from, to) => {
            if (from && to) {
                getData(from, to);
                setDateRange({ from, to });
            }
        },
        [getData]
    );

    const handleChangeSelected = useCallback((values: SelectionMap) => {
        setSelectionMap(values);
    }, []);

    return (
        <>
            <div className={clsx(classes.p15, classes.flexRow)}>
                <DateFilters
                    dateFilter={dateFilter}
                    setDateFilter={setDateFilter}
                    getData={handleDateChange}
                    selectedDatesHistory={[]}
                    setSelectedDatesHistory={() => {}}
                    showClosing={false}
                />
                <div
                    className={clsx(classes.mLeft15, classes.flexRowEnd)}
                    style={{ flex: '2 0 200px' }}
                >
                    <Button
                        disableRipple
                        variant="outlined"
                        color="primary"
                        startIcon={isSelectMode ? <CloseIcon /> : <AssessmentOutlinedIcon />}
                        onClick={() => handleToggleSelectMode(!isSelectMode)}
                    >
                        {isSelectMode ? i18n.t('button.close') : i18n.t('common.generateReport')}
                    </Button>
                </div>
            </div>

            <div
                className={clsx(
                    classes.flexRowEnd,
                    classes.mBot15,
                    classes.mRight15,
                    classes.mLeft15
                )}
            >
                <StockRestaurantSelect
                    style={{ flex: '1 0 50%', maxWidth: 300 }}
                    groups={extendedGroups}
                    value={selectionMap}
                    onChange={handleChangeSelected}
                />
            </div>

            <TableContainer style={{ flex: 1 }}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            {isSelectMode && (
                                <TableCell>
                                    <Checkbox
                                        indeterminate={
                                            selected.size > 0 &&
                                            selected.size < filteredDishes.length
                                        }
                                        checked={
                                            selected.size > 0 &&
                                            selected.size === filteredDishes.length
                                        }
                                        onChange={(_, checked) => handleSelectAll(checked)}
                                        inputProps={{ 'aria-label': 'select all' }}
                                    />
                                </TableCell>
                            )}
                            <TableCell
                                sortDirection={orderBy === 'name' ? order : false}
                                width={280}
                            >
                                <div className={classes.flexRow}>
                                    <TableSortLabel
                                        className={classes.mRight10}
                                        active={orderBy === 'name'}
                                        direction={orderBy === 'name' ? order : 'asc'}
                                        onClick={() => handleRequestSort('name')}
                                    ></TableSortLabel>
                                    <div style={{ position: 'relative', bottom: 8 }}>
                                        <SearchInput
                                            fontSize={16}
                                            name={i18n.t('form.itemName')}
                                            value={filter.name}
                                            onChange={(val) => setFilter({ ...filter, name: val })}
                                            onFocus={() => {}}
                                            onEnter={() => ''}
                                            onReset={() => setFilter({ ...filter, name: '' })}
                                            trimValue={false}
                                        />
                                    </div>
                                </div>
                            </TableCell>
                            <TableCell
                                sortDirection={orderBy === 'externalId' ? order : false}
                                width={150}
                                style={{ maxWidth: 150, minWidth: 140 }}
                            >
                                <div className={classes.flexRow}>
                                    <TableSortLabel
                                        className={classes.mRight10}
                                        active={orderBy === 'externalId'}
                                        direction={orderBy === 'externalId' ? order : 'asc'}
                                        onClick={() => handleRequestSort('externalId')}
                                    ></TableSortLabel>
                                    <div style={{ position: 'relative', bottom: 8 }}>
                                        <SearchInput
                                            fontSize={16}
                                            name={i18n.t('form.externalId')}
                                            value={filter.externalId}
                                            onChange={(val) =>
                                                setFilter({ ...filter, externalId: val })
                                            }
                                            onFocus={() => {}}
                                            onEnter={() => ''}
                                            onReset={() => setFilter({ ...filter, externalId: '' })}
                                            trimValue={false}
                                        />
                                    </div>
                                </div>
                            </TableCell>
                            <TableCell
                                sortDirection={orderBy === 'supplierName' ? order : false}
                                width={180}
                                style={{ maxWidth: 180, minWidth: 140 }}
                            >
                                <div className={classes.flexRow}>
                                    <TableSortLabel
                                        className={classes.mRight10}
                                        active={orderBy === 'supplierName'}
                                        direction={orderBy === 'supplierName' ? order : 'asc'}
                                        onClick={() => handleRequestSort('supplierName')}
                                    ></TableSortLabel>
                                    <div style={{ position: 'relative', bottom: 8 }}>
                                        <SearchInput
                                            fontSize={16}
                                            name={i18n.t('form.supplier')}
                                            value={filter.supplierName}
                                            onChange={(val) =>
                                                setFilter({ ...filter, supplierName: val })
                                            }
                                            onFocus={() => {}}
                                            onEnter={() => ''}
                                            onReset={() =>
                                                setFilter({ ...filter, supplierName: '' })
                                            }
                                            trimValue={false}
                                        />
                                    </div>
                                </div>
                            </TableCell>
                            <TableCell
                                className={classes.tableHeader}
                                align="right"
                                sortDirection={orderBy === 'price' ? order : false}
                            >
                                <TableSortLabel
                                    active={orderBy === 'price'}
                                    direction={orderBy === 'price' ? order : 'asc'}
                                    onClick={() => handleRequestSort('price')}
                                >
                                    {i18n.t('common.price')}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell
                                className={classes.tableHeader}
                                align="right"
                                sortDirection={orderBy === 'sold' ? order : false}
                            >
                                <TableSortLabel
                                    active={orderBy === 'sold'}
                                    direction={orderBy === 'sold' ? order : 'asc'}
                                    onClick={() => handleRequestSort('sold')}
                                >
                                    {i18n.t('common.sold')}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell
                                className={classes.tableHeader}
                                align="right"
                                sortDirection={orderBy === 'stockQuantity' ? order : false}
                            >
                                <TableSortLabel
                                    active={orderBy === 'stockQuantity'}
                                    direction={orderBy === 'stockQuantity' ? order : 'asc'}
                                    onClick={() => handleRequestSort('stockQuantity')}
                                >
                                    {i18n.t('form.stock')}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell
                                className={classes.tableHeader}
                                align="right"
                                sortDirection={orderBy === 'minimumStock' ? order : false}
                            >
                                <TableSortLabel
                                    active={orderBy === 'minimumStock'}
                                    direction={orderBy === 'minimumStock' ? order : 'asc'}
                                    onClick={() => handleRequestSort('minimumStock')}
                                >
                                    {i18n.t('form.minStock')}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell
                                sortDirection={orderBy === 'catalogName' ? order : false}
                                width={150}
                                style={{ maxWidth: 150, minWidth: 120 }}
                            >
                                <div className={classes.flexRow}>
                                    <TableSortLabel
                                        className={classes.mRight10}
                                        active={orderBy === 'catalogName'}
                                        direction={orderBy === 'catalogName' ? order : 'asc'}
                                        onClick={() => handleRequestSort('catalogName')}
                                    ></TableSortLabel>
                                    <div style={{ position: 'relative', bottom: 8 }}>
                                        <SearchInput
                                            fontSize={16}
                                            name={i18n.t('restaurant.catalog.one')}
                                            value={filter.catalogName}
                                            onChange={(val) =>
                                                setFilter({ ...filter, catalogName: val })
                                            }
                                            onFocus={() => {}}
                                            onEnter={() => ''}
                                            onReset={() =>
                                                setFilter({ ...filter, catalogName: '' })
                                            }
                                            trimValue={false}
                                        />
                                    </div>
                                </div>
                            </TableCell>
                            <TableCell
                                className={classes.tableHeader}
                                align="right"
                                sortDirection={orderBy === 'restaurantName' ? order : false}
                                width={150}
                                style={{ maxWidth: 150 }}
                            >
                                <TableSortLabel
                                    active={orderBy === 'restaurantName'}
                                    direction={orderBy === 'restaurantName' ? order : 'asc'}
                                    onClick={() => handleRequestSort('restaurantName')}
                                >
                                    {i18n.t('form.restaurant')}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="right"></TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {isLoading && (
                            <TableRow>
                                <TableCell colSpan={100} align="center">
                                    <CircularProgress size={48} className={classes.p15} />
                                </TableCell>
                            </TableRow>
                        )}
                        {!isLoading &&
                            filteredDishes.sort(sortData).map((dish) => {
                                return (
                                    <TableRow key={makeKey(dish.restaurantId, dish.id)}>
                                        {isSelectMode && (
                                            <TableCell>
                                                <Checkbox
                                                    checked={selected.has(
                                                        makeKey(dish.restaurantId, dish.id)
                                                    )}
                                                    onChange={(_, checked) =>
                                                        handleSelectDish(checked, dish)
                                                    }
                                                    inputProps={{ 'aria-label': 'select dish' }}
                                                />
                                            </TableCell>
                                        )}
                                        <TableCell width={280}>
                                            <Text
                                                text={dish.name || ''}
                                                maxLength={textLimit.L}
                                                fontSize={14}
                                                withoutMore
                                            />
                                        </TableCell>
                                        <TableCell width={150} style={{ maxWidth: 150 }}>
                                            <Text
                                                text={dish.externalId?.toString() || ''}
                                                maxLength={textLimit.S}
                                                fontSize={14}
                                                withoutMore
                                            />
                                        </TableCell>
                                        <TableCell width={180} style={{ maxWidth: 180 }}>
                                            <Text
                                                text={dish.supplierName || ''}
                                                maxLength={textLimit.S}
                                                fontSize={14}
                                                withoutMore
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            {dish.price.toFixed(2)}
                                            {dish.additionalPrices.map((x) => ` | ${x.price}`)}
                                        </TableCell>
                                        <TableCell align="right">
                                            {dish.sold}
                                            {dish.additionalPrices.map((x) => ` | ${x.sold}`)}
                                        </TableCell>
                                        <TableCell align="right">{dish.stockQuantity}</TableCell>
                                        <TableCell align="right">{dish.minimumStock}</TableCell>
                                        <TableCell align="right" width={150}>
                                            <Text
                                                text={dish.catalogName || ''}
                                                maxLength={textLimit.S}
                                                fontSize={14}
                                                withoutMore
                                            />
                                        </TableCell>
                                        <TableCell
                                            align="right"
                                            width={150}
                                            style={{ maxWidth: 150 }}
                                        >
                                            <Text
                                                text={dish.restaurantName || ''}
                                                maxLength={textLimit.M}
                                                fontSize={14}
                                                withoutMore
                                            />
                                        </TableCell>
                                        <TableCell align="right"></TableCell>
                                    </TableRow>
                                );
                            })}
                        {!isLoading && dishes.filter(filterData).length === 0 && (
                            <TableRow>
                                <TableCell colSpan={100} style={{ height: 550 }}>
                                    <NothingFoundMessage />
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Collapse in={isSelectMode}>
                <Paper className={classes.bottomAppBar}>
                    <div className={classes.flexRowSpaceBetween}>
                        <div className={classes.mRight15}>
                            {selected.size} {i18n.t('common.itemsSelected')?.toLowerCase()}
                        </div>

                        <div className={classes.flexRow}>
                            <Button
                                className={classes.mRight15}
                                variant="outlined"
                                color="primary"
                                startIcon={<CloseIcon />}
                                onClick={() => handleToggleSelectMode(false)}
                            >
                                {i18n.t('button.cancel')}
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<GetAppIcon />}
                                disabled={selected.size === 0}
                                onClick={handleExportReport}
                            >
                                {i18n.t('common.excelExport')}
                            </Button>
                        </div>
                    </div>
                </Paper>
            </Collapse>
        </>
    );
}
